import * as iots from "io-ts";
import type { TableName } from "./database-strings";
import { areTableNamesEqual, tableNameCodec } from "./database-strings";

const googleSheetSourceMetadata = iots.intersection([
    iots.type({
        type: iots.literal("Google Sheet"),
        title: iots.string,
        id: iots.string,
    }),
    iots.partial({
        fromSharedDrive: iots.boolean,
    }),
]);
export type GoogleSheetSourceMetadata = iots.TypeOf<typeof googleSheetSourceMetadata>;

// It's ok if this is missing full provenance because it's just used for
// display in the frontend.
export const externalSource = iots.union([
    iots.intersection([
        iots.type({
            type: iots.literal("excel-online"),
            url: iots.string,
            workbookID: iots.string,
            userID: iots.string,
        }),
        iots.partial({
            workbookName: iots.string,
        }),
    ]),
    iots.type({
        type: iots.literal("airtable"),
        url: iots.string,
        userID: iots.string,
        baseID: iots.string,
    }),
    iots.type({
        type: iots.literal("mysql-gcp"),
        instanceID: iots.string,
        url: iots.string,
    }),
    iots.type({
        type: iots.literal("unknown"),
    }),
]);
export type ExternalSource = iots.TypeOf<typeof externalSource>;

export const nativeTableSourceMetadata = iots.intersection([
    iots.type({
        type: iots.literal("Native table"),
        id: iots.string,
        tableName: tableNameCodec,
    }),
    iots.partial({
        externalSource,
    }),
]);

export const externalSourceMetadata = iots.type({
    type: iots.literal("Native table"),
    id: iots.string,
    tableName: tableNameCodec,
    externalSource,
});

export type ExternalSourceMetadata = iots.TypeOf<typeof externalSourceMetadata>;

export type NativeTableSourceMetadata = iots.TypeOf<typeof nativeTableSourceMetadata>;

export const sourceMetadataCodec = iots.union([googleSheetSourceMetadata, nativeTableSourceMetadata]);
export type SourceMetadata = iots.TypeOf<typeof sourceMetadataCodec>;

export function getGoogleSheetsSourceMetadata(
    sourceMetadata: readonly SourceMetadata[]
): GoogleSheetSourceMetadata | undefined {
    for (const sm of sourceMetadata) {
        if (sm.type === "Google Sheet") {
            return sm;
        }
    }
    return undefined;
}

export function getNativeTableSourceMetadata(
    sourceMetadata: readonly SourceMetadata[],
    tableName: TableName
): NativeTableSourceMetadata | undefined {
    return sourceMetadata.find(
        sm => sm.type === "Native table" && areTableNamesEqual(sm.tableName, tableName)
    ) as NativeTableSourceMetadata;
}

export function getSourceMetadataForNativeTable(
    sourceMetadata: readonly SourceMetadata[],
    nativeTableID: string
): NativeTableSourceMetadata | undefined {
    for (const sm of sourceMetadata) {
        if (sm.type === "Native table" && sm.id === nativeTableID) {
            return sm;
        }
    }
    return undefined;
}

export function getNativeTableSourceMetadatas(sourceMetadata: readonly SourceMetadata[]): NativeTableSourceMetadata[] {
    return sourceMetadata.filter(sm => sm.type === "Native table") as NativeTableSourceMetadata[];
}

export function isNativeTable(sourceMetadata: readonly SourceMetadata[], tableName: TableName): boolean {
    return getNativeTableSourceMetadata(sourceMetadata, tableName) !== undefined;
}
